import { useEffect, useState } from "react";
import { Button, IconButton, Paper, Switch, Tooltip, Typography } from "@mui/material";
import PropTypes from "prop-types";

import ProgressBar from "src/components/layout/Progress/ProgressBar";
import { useUserData } from "src/context/userContext";
import { attachment_upload } from "./AttachmentFunctions";
import { pumiFi_attachment_upload } from "src/data/pumiFi/pumiFiClass";
import { v4 as uuidv4 } from "uuid";
import { aikaPvmStringLong } from "src/components/js/statics1";
import { CloseIcon } from "src/components/icons/ActionIcons";
import { allowAccess_KalleUrhoOy_Toiminnot } from "src/data/ifs/allowAccess";
import styled from "styled-components";
import ProgressCirc from "../layout/Progress/ProgressCirc";

const ProcessLogContainer = styled(Paper)`
  font-family: monospace;
  padding: 1px;
  margin-top: 1px;
  text-align: left;
  font-size: 0.8rem;
  width: 100%;
  position: relative;
  border: ${(props) => (props.$uploadError ? "1px solid red" : "none")};
  border-radius: 4px;
`;

export default function AttachmentUploader({ onUpload = () => {}, keikkaId = 0 }) {
  const { token, userAsiakas, user } = useUserData();

  const [dragging, setDragging] = useState(false);
  const [isLoadingFileName, setIsLoadingFileName] = useState(null);
  const [isUploadToPumiFiChecked, setIsUploadToPumiFiChecked] = useState(true);
  const [processLog, setProcessLog] = useState([]);
  const [uploadError, setUploadError] = useState(false);

  // const [uploadingIndexNumber, setUploadingIndexNumber] = useState(0);
  const [uploadTotalBytes, setUploadTotalBytes] = useState(0);
  const [uploadedBytes, setUploadedBytes] = useState(0);

  const handleFileChange = async (event) => {
    const input = event.target;
    await uploadFiles(event.target.files);
    input.value = null;
  };

  useEffect(() => {
    const uploadAlsoToPumiFi = allowAccess_KalleUrhoOy_Toiminnot(user);
    setIsUploadToPumiFiChecked(uploadAlsoToPumiFi);
  }, [user]);

  const addLog = (log) => {
    console.log(log);
    setProcessLog((prevLogs) => [...prevLogs, log]);
  };

  const uploadFiles = async (files) => {
    setUploadError(false);
    if (files.length === 0) {
      setProcessLog([]);
      addLog("EI TIEDOSTOJA");
      addLog("Yhtään tiedostoa ei löytynyt.");
      setUploadError(true);
      return;
    }
    addLog(aikaPvmStringLong(new Date(), true) + " Aloitetaan tiedostojen siirto...");
    const startTime = new Date().getTime();
    setProcessLog([]);
    // count total bytes
    let _bytes = 0;
    for (let i = 0; i < files.length; i++) {
      _bytes += files[i].size;
    }
    if (isUploadToPumiFiChecked) _bytes *= 2;
    setUploadTotalBytes(_bytes);

    for (let i = 0; i < files.length; i++) {
      addLog("- Lähetetään tiedosto " + files[i].name);
      const file = files[i];
      // create new filename as guid for each file
      const newFileName = uuidv4() + files[i].name.substring(file.name.lastIndexOf("."));
      const origFileName = file.name;
      setIsLoadingFileName(origFileName);
      const ownerAsiakasId = userAsiakas.asiakasId;
      const fileComment = null;

      try {
        const attachmentId = await attachment_upload(
          file,
          keikkaId,
          0,
          0,
          0,
          0,
          0,
          ownerAsiakasId,
          newFileName,
          fileComment,
          user.personId,
          token
        );
        const size = file.size;
        setUploadedBytes((prev) => prev + size);
        if (attachmentId > 0 && isUploadToPumiFiChecked) {
          addLog("- Lähetetään tiedosto PUMI.fi palveluun");
          await pumiFi_attachment_upload(file, newFileName, attachmentId, origFileName, token);
          setUploadedBytes((prev) => prev + size);
        }
      } catch (error) {
        addLog("Virhe tiedoston " + files[i].name + " siirrossa: " + error);
        setUploadError(true);
      }
    }
    // calculate how long it took to upload all files
    const endTime = new Date().getTime();
    const timeDiff = Math.round((endTime - startTime) / 1000);
    addLog("Kesto: " + timeDiff + " s");
    addLog(
      aikaPvmStringLong(new Date(), true) +
        " Kaikki " +
        files.length +
        " tiedostoa lähetetty (" +
        timeDiff +
        " s)"
    );
    setUploadedBytes(0);
    setIsLoadingFileName(null);
    onUpload(files);
  };
  const handleCloseClick = () => {
    setProcessLog([]);
    setIsLoadingFileName(null);
    setDragging(false);
  };
  const handleDragOver = (event) => {
    event.preventDefault();
    setDragging(true);
  };
  const handleDrop = async (event) => {
    event.preventDefault();
    setDragging(false);
    await uploadFiles(event.dataTransfer.files);
  };

  return (
    <div>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setDragging(false)}
        style={{
          border: dragging ? "2px dashed #3f51b5" : "3px dashed #ccc",
          padding: "35px",
          textAlign: "center",
          borderRadius: "4px",
          transition: "border-color 0.3s",
        }}
        className="harmaaTausta">
        <input
          accept=".jpg,.pdf"
          style={{ display: "none" }}
          id="file-upload"
          type="file"
          onChange={handleFileChange}
          multiple
        />
        <label htmlFor="file-upload">
          <Button variant="contained" color="primary" component="span" sx={{ margin: "10px" }}>
            VALITSE TIEDOSTOT
          </Button>
        </label>

        {isLoadingFileName && (
          <div className="center" style={{ gap: 4 }}>
            <ProgressCirc />
            <Typography variant="body" color="primary">
              Lähetetään tiedosto {isLoadingFileName}
            </Typography>
          </div>
        )}
        {!isLoadingFileName && (
          <>
            <Typography variant="body2" color="primary">
              tai vedä ja pudota tiedostot tänne
            </Typography>
            <Typography variant="body2" color="secondary">
              (Sallitut tiedostomuodot: .jpg, .pdf)
            </Typography>
          </>
        )}
      </div>
      {renderProgressBars()}
      {renderProcessLog()}
      {allowAccess_KalleUrhoOy_Toiminnot(user) && (
        <>
          <Switch
            checked={isUploadToPumiFiChecked}
            onChange={() => setIsUploadToPumiFiChecked(!isUploadToPumiFiChecked)}
          />
          <Typography
            variant="body"
            color="primary"
            sx={{ opacity: isUploadToPumiFiChecked ? 0.9 : 0.4 }}>
            {" "}
            Lähetä myös PUMI.fi palveluun
          </Typography>
        </>
      )}
    </div>
  );

  function renderProcessLog() {
    return (
      <>
        {processLog.length > 0 && (
          <ProcessLogContainer elevation={7} $uploadError={uploadError}>
            <Tooltip disableInteractive title="Sulje">
              <IconButton onClick={handleCloseClick} style={{ position: "absolute", right: 5 }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
            {processLog.map((logText, index) => (
              <div key={index}>{logText}</div>
            ))}
          </ProcessLogContainer>
        )}
      </>
    );
  }

  function renderProgressBars() {
    if (isLoadingFileName === null) return null;
    return (
      <div style={{ width: "100%", padding: "4px" }}>
        {processLog.length > 0 ? (
          <ProgressBar
            show={isLoadingFileName !== null}
            variant="determinate"
            value={100 * (uploadedBytes / uploadTotalBytes)}
          />
        ) : (
          <ProgressBar show={isLoadingFileName !== null} />
        )}
      </div>
    );
  }
}

AttachmentUploader.propTypes = {
  onUpload: PropTypes.func,
  keikkaId: PropTypes.number,
};
